import { useState } from "react";
import LogLayout from "../components/LogLayout";
import {
  BookOpen, FileText, CreditCard, Send, Building2, Brain,
  MessageSquare, Zap, BarChart3, Archive, Bell, ChevronDown, ChevronRight,
  Sparkles, Package, HelpCircle, CheckCircle2, Plug, AlertOctagon, Mail, Calendar
} from "lucide-react";

const GUIDE_SECTIONS = [
  {
    id: "start",
    icon: Sparkles,
    title: "Démarrage rapide",
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    steps: [
      { title: "Créer votre première facture", text: "Allez dans 'Dossiers Factures' via le menu FACTURATION. Cliquez 'Nouveau dossier', scannez ou uploadez votre facture. L'OCR extrait automatiquement les informations (numéro, assureur, montant, date)." },
      { title: "Vérifier les informations", text: "Si l'OCR a mal lu certaines informations, utilisez l'onglet 'Corriger OCR' dans le dossier pour corriger manuellement chaque champ." },
      { title: "Suivre vos factures dans le Pipeline", text: "Le Pipeline (menu FACTURATION) affiche toutes vos factures sous forme Kanban avec 6 étapes : À déposer, Déposée, Chèque à récupérer, Bordereau à récupérer, Remise de chèque, Encaissé. Le pipeline se met à jour automatiquement à chaque changement de statut." },
      { title: "Consulter votre tableau de bord", text: "Le Tableau de bord affiche vos KPIs principaux, le pipeline des factures, les prévisions d'encaissement, les alertes intelligentes et la trésorerie prévisionnelle." },
    ]
  },
  {
    id: "invoices",
    icon: FileText,
    title: "Dossiers Factures",
    color: "text-sky-600",
    bg: "bg-sky-50",
    steps: [
      { title: "Scanner une facture", text: "Uploadez une photo ou un scan de la facture. Le système extrait automatiquement : numéro, assureur, montant, date via l'intelligence artificielle (OCR)." },
      { title: "Corriger les informations OCR", text: "Si le scan est illisible, ouvrez le dossier, onglet 'Corriger OCR'. Vous pouvez voir la photo en grand et corriger chaque champ individuellement." },
      { title: "Statuts de facture", text: "Chaque facture passe par des statuts : Éditée (créée), Déposée (envoyée à l'assureur), Paiement partiel (partiellement payée), Paiement total/effectué (soldée), ou Rejetée. Le changement de statut met automatiquement à jour la position dans le Pipeline." },
      { title: "Ajouter des documents", text: "Onglet 'Documents' pour ajouter des pièces justificatives, ordonnances ou compléments au dossier." },
    ]
  },
  {
    id: "pipeline",
    icon: Package,
    title: "Pipeline des Factures",
    color: "text-cyan-600",
    bg: "bg-cyan-50",
    steps: [
      { title: "Vue Kanban", text: "Le Pipeline affiche vos factures en 6 colonnes : À déposer, Déposée, Chèque à récupérer, Bordereau à récupérer, Remise de chèque, Encaissé. Les factures avancent automatiquement selon leur statut." },
      { title: "Synchronisation des statuts", text: "Quand vous changez le statut d'une facture (ex: paiement partiel), elle se déplace automatiquement dans la bonne colonne du Pipeline. Les montants et compteurs se mettent à jour en temps réel." },
      { title: "Suivi paiements par assureur", text: "Onglet 'Suivi paiements' dans le Pipeline. Visualisez les créances par assureur : montant total, payé, encours, taux de recouvrement, délai moyen, factures en retard (>45j) et critiques (>90j)." },
      { title: "Détail par assureur", text: "Cliquez l'icône détail pour voir chaque facture d'un assureur avec son statut réel (Éditée, Déposée, Paiement partiel...), l'étape pipeline, le montant payé/encours et le délai." },
      { title: "Export PDF", text: "Exportez le suivi des paiements par assureur en PDF pour vos rapports et réunions." },
    ]
  },
  {
    id: "recovery",
    icon: CreditCard,
    title: "Créances & Trésorerie",
    color: "text-amber-600",
    bg: "bg-amber-50",
    steps: [
      { title: "Vue d'ensemble des créances", text: "Montant total à recouvrer, montant déjà recouvré, nombre d'assureurs, taux de non-recouvrement et ratio créances/chiffre d'affaires. Filtrage par période : ce mois, 3 mois, 6 mois, 12 mois, tout." },
      { title: "Créances par assureur", text: "Graphique à barres et tableau détaillé montrant les créances pour chaque assureur avec le nombre de factures, montant à recouvrer et montant déjà payé." },
      { title: "Plafonds assureur", text: "Définissez un plafond de créances par assureur. Le système vous alerte quand les créances approchent ou dépassent le plafond (seuil d'alerte configurable)." },
      { title: "Trésorerie prévisionnelle", text: "La section Trésorerie affiche le ratio créances/CA mensuel et l'évolution mensuelle des créances vs recouvrements. Renseignez votre chiffre d'affaires mensuel pour des prévisions précises." },
      { title: "Encaissements prévus", text: "L'onglet Encaissements montre un calendrier des encaissements prévus, calculés à partir des délais de paiement de chaque assureur." },
    ]
  },
  {
    id: "expeditions",
    icon: Send,
    title: "Expéditions & Logistique",
    color: "text-violet-600",
    bg: "bg-violet-50",
    steps: [
      { title: "Créer une expédition", text: "Menu LOGISTIQUE > Expéditions. Demandez l'envoi de documents à un assureur. Un agent commercial sera automatiquement assigné depuis le pool." },
      { title: "Suivre l'expédition", text: "Suivez le statut en temps réel : en attente, en cours, livrée. Recevez une notification à chaque changement de statut." },
      { title: "Bordereaux", text: "Menu LOGISTIQUE > Bordereaux. Gérez vos bordereaux de remise de factures groupés par assureur." },
      { title: "Suivi GPS", text: "Menu LOGISTIQUE > Suivi GPS. Visualisez la position des agents commerciaux en temps réel sur une carte." },
    ]
  },
  {
    id: "courses",
    icon: Package,
    title: "Courses Pharmacie",
    color: "text-pink-600",
    bg: "bg-pink-50",
    steps: [
      { title: "Confier une course", text: "Menu LOGISTIQUE > Courses. Besoin d'envoyer un colis, un document ou faire une course ? Créez une demande et un agent commercial sera assigné." },
      { title: "Suivre la course", text: "Même suivi en temps réel que pour les expéditions. L'historique complet est accessible à tout moment." },
    ]
  },
  {
    id: "insurers",
    icon: Building2,
    title: "Gestion des Assureurs",
    color: "text-teal-600",
    bg: "bg-teal-50",
    steps: [
      { title: "Ajouter un assureur", text: "Menu ASSUREURS > Gestion Assureurs. Ajoutez les compagnies d'assurance avec leurs coordonnées et configurez leurs délais de paiement (nombre de jours)." },
      { title: "Configurer les délais", text: "Chaque assureur a un 'Délai de traitement' (jours pour traiter la facture) et un 'Délai disponibilité chèque' (jours supplémentaires). Ces délais sont utilisés pour les prévisions d'encaissement." },
      { title: "Classement assureurs", text: "Menu ASSUREURS > Classement. Comparez les assureurs par taux de recouvrement, volume de factures et délais de paiement." },
    ]
  },
  {
    id: "messaging",
    icon: MessageSquare,
    title: "Messagerie Assureurs",
    color: "text-sky-600",
    bg: "bg-sky-50",
    steps: [
      { title: "Communiquer sur une facture", text: "Menu ASSUREURS > Messagerie. Échangez directement avec l'assureur concerné par une facture. Chaque conversation est liée à un dossier." },
      { title: "Notifications", text: "Vous recevez une notification quand l'assureur répond. Les messages non lus sont indiqués par un badge." },
    ]
  },
  {
    id: "reports",
    icon: BarChart3,
    title: "Rapports & Analytics",
    color: "text-violet-600",
    bg: "bg-violet-50",
    steps: [
      { title: "Rapport mensuel", text: "Menu ANALYSE & IA > Rapports. Un rapport est généré automatiquement chaque mois avec vos KPIs. Téléchargez-le en PDF avec le logo CMES et votre cachet." },
      { title: "Analyse IA", text: "Cliquez 'Générer l'analyse IA' sur un rapport pour obtenir un résumé exécutif, 3 recommandations et un score de santé sur 100, générés par intelligence artificielle." },
      { title: "Dashboard IA", text: "Menu ANALYSE & IA > Dashboard IA. Visualisez l'évolution de votre score sur 12 mois avec des alertes proactives et des tendances." },
      { title: "Analytics comparatif", text: "Menu ANALYSE & IA > Analytics. Comparez vos performances avec le marché : par ville, commune, assureur, avec des graphiques interactifs et un classement anonymisé." },
      { title: "Export hebdomadaire", text: "Un email automatique résume vos KPIs chaque lundi matin : factures, recouvrement, alertes en cours. Configurable depuis votre tableau de bord." },
    ]
  },
  {
    id: "alerts",
    icon: Zap,
    title: "Alertes Intelligentes",
    color: "text-amber-600",
    bg: "bg-amber-50",
    steps: [
      { title: "Suivi automatique", text: "Le système détecte automatiquement les retards de paiement en comparant la date de dépôt de chaque facture avec le délai configuré pour l'assureur. Pas besoin de vérifier manuellement." },
      { title: "Retards et critiques", text: "Les factures sont classées : Normal (<45 jours), En retard (45-90 jours), Critique (>90 jours). Ces indicateurs sont visibles dans le Pipeline (Suivi paiements) et les Créances." },
      { title: "Relancer un assureur", text: "Depuis le widget d'alertes ou la page dédiée, relancez directement l'assureur en retard en un clic. Un email et/ou SMS de relance est envoyé automatiquement." },
      { title: "Niveaux de sévérité", text: "Les alertes sont classées par sévérité : 'warning' (retard modéré) et 'critical' (retard important). Les alertes critiques sont mises en évidence avec un badge rouge." },
    ]
  },
  {
    id: "api-caisse",
    icon: Plug,
    title: "API Caisse (POS)",
    color: "text-indigo-600",
    bg: "bg-indigo-50",
    steps: [
      { title: "Qu'est-ce que l'API Caisse ?", text: "L'API Caisse permet à votre logiciel de gestion officinale (LGO) de communiquer directement avec CMES-LOG. Les factures générées par votre caisse sont automatiquement importées dans le système." },
      { title: "Configuration", text: "Générez une clé API depuis votre espace de configuration. Communiquez cette clé à votre prestataire LGO pour qu'il configure l'intégration." },
      { title: "Fonctionnement", text: "Quand une facture assurance est émise par votre caisse, elle est automatiquement créée dans CMES-LOG avec toutes les informations (patient, assureur, montant, produits). Plus besoin de scanner !" },
      { title: "Endpoints disponibles", text: "POST /api/pos/invoice : Créer une facture. GET /api/pos/invoices : Lister les factures. GET /api/pos/status/:id : Vérifier le statut d'une facture. Voir la documentation technique pour plus de détails." },
    ]
  },
  {
    id: "signalement",
    icon: AlertOctagon,
    title: "Signalement",
    color: "text-red-600",
    bg: "bg-red-50",
    steps: [
      { title: "Signaler un problème", text: "Utilisez la fonction Signalement pour rapporter tout problème détecté : facture incorrecte, montant erroné, assureur qui ne répond pas, ou tout dysfonctionnement du système." },
      { title: "Suivi du signalement", text: "Chaque signalement reçoit un numéro de suivi. Vous pouvez consulter l'état d'avancement de vos signalements à tout moment (en cours, résolu, fermé)." },
      { title: "Types de signalements", text: "Problème de facturation, litige avec un assureur, erreur de montant, document manquant, problème technique. Chaque type est automatiquement transmis au service concerné." },
      { title: "Escalade automatique", text: "Si un signalement n'est pas traité dans les délais, il est automatiquement escaladé au niveau supérieur pour garantir une résolution rapide." },
    ]
  },
  {
    id: "email-export",
    icon: Mail,
    title: "Export Email Hebdomadaire",
    color: "text-sky-600",
    bg: "bg-sky-50",
    steps: [
      { title: "Résumé automatique", text: "Chaque lundi matin, un email résumé est envoyé avec vos KPIs de la semaine précédente : nouvelles factures, recouvrements, alertes actives, et prévisions d'encaissement." },
      { title: "Contenu de l'email", text: "L'email inclut : nombre de factures émises/payées, montant total recouvré, taux de recouvrement, alertes de retard en cours, et les encaissements prévus pour la semaine à venir." },
      { title: "Configuration", text: "L'export hebdomadaire est activé par défaut pour toutes les pharmacies. Vous pouvez le désactiver depuis les préférences de notifications." },
    ]
  },
];

function GuideSection({ section }) {
  const [open, setOpen] = useState(false);
  const Icon = section.icon;

  return (
    <div data-testid={`guide-${section.id}`} className="bg-white border border-slate-200 rounded-xl overflow-hidden transition-all hover:border-slate-300 shadow-sm">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 p-4 text-left">
        <div className={`w-10 h-10 rounded-xl ${section.bg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${section.color}`} />
        </div>
        <h3 className="text-sm font-bold text-slate-800 flex-1">{section.title}</h3>
        <span className="text-[10px] text-slate-400 mr-2">{section.steps.length} étapes</span>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
          {section.steps.map((step, i) => (
            <div key={i} className="flex gap-3 pl-2">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-50 border border-sky-200 flex items-center justify-center mt-0.5">
                <span className="text-[10px] font-bold text-sky-600">{i + 1}</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-700">{step.title}</p>
                <p className="text-xs text-slate-500 leading-relaxed mt-0.5">{step.text}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LogGuidePage() {
  return (
    <LogLayout title="Guide d'utilisation" subtitle="Apprenez a utiliser CMES-LOG efficacement">
      <div data-testid="guide-page" className="space-y-4 max-w-3xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-sky-50 to-violet-50 border border-sky-200 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <HelpCircle className="w-8 h-8 text-sky-500" />
            <div>
              <h2 className="text-base font-bold text-slate-800">Bienvenue sur CMES-LOG</h2>
              <p className="text-xs text-slate-500">Cliquez sur chaque section pour decouvrir comment l'utiliser. {GUIDE_SECTIONS.length} sections disponibles.</p>
            </div>
          </div>
        </div>

        {/* Sections */}
        {GUIDE_SECTIONS.map(s => (
          <GuideSection key={s.id} section={s} />
        ))}

        {/* Support */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 text-center shadow-sm">
          <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
          <p className="text-sm font-semibold text-slate-800">Besoin d'aide supplementaire ?</p>
          <p className="text-xs text-slate-500 mt-1">Contactez notre equipe support : <span className="text-sky-500">support@cmes.ci</span> ou appelez le <span className="text-sky-500">07 68 12 70 00</span></p>
        </div>
      </div>
    </LogLayout>
  );
}
